import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Tooltip, message } from "antd";
import redToggle from "../../../../../assets/img/icons/table/toggleSpecial.svg";
import greenToggle from "../../../../../assets/img/icons/table/toggleSpecialgreen.svg";
import { openModal, closeModal } from "../../../../../data/slices/modals";
import {
  checkTresoHasNonPointedStatus,
  checkComptaHasNonReelTypes,
  lockTresoPeriod,
  lockComptaPeriod,
  unlockTresoPeriod,
  unlockComptaPeriod,
} from "../../../../../data/slices/projectLock";
import { convertToYYYYMM } from "../../../../../utilities/filters";

function LockIcon({ lockStatus, isDisplayOnly, months }) {
  const dispatch = useDispatch();
  const { id: projectId } = useParams();

  const { RegroupmentValue } = useSelector((state) => state.categories);
  const { mode } = useSelector((state) => state.layout);

  if (!lockStatus || lockStatus === "cannot be locked") {
    return null;
  }

  let tooltipMessage =
    "Ce mois est déverrouillé. Cliquer pour verrouiller.";
  if (isDisplayOnly) {
    tooltipMessage =
      lockStatus === "locked" ? "Ce mois est verrouillé." : "";
  } else if (RegroupmentValue !== 1) {
    tooltipMessage =
      "La fonction verrouillage / déverrouillage n'est accessible qu'en mode affichage « mois par mois ».";
  } else if (lockStatus === "locked") {
    tooltipMessage = "Ce mois est verrouillé. Cliquer pour déverrouiller.";
  }

  const handleLockClick = async () => {
    if (RegroupmentValue !== 1) {
      return;
    }

    const monthForApi = months && months.length > 0 ? months[0] : null;

    if (!monthForApi) {
      console.error("No month available for API call");
      return;
    }

    try {
      if (mode === "treso") {
        if (lockStatus === "locked") {
          // Already locked - show unlock confirmation directly
          dispatch(
            openModal("treso-unlock-confirm-modal", {
              onConfirm: async () => {
                try {
                  await dispatch(
                    unlockTresoPeriod({
                      projectId,
                      body: {
                        unlockMonth: convertToYYYYMM(monthForApi),
                        mode,
                      },
                    })
                  ).unwrap();

                  message.success(
                    `Période ${monthForApi} déverrouillée avec succès`
                  );

                  dispatch(closeModal("treso-unlock-confirm-modal"));
                } catch (error) {
                  console.error("Error unlocking treso period:", error);
                  message.error("Erreur lors du déverrouillage de la période");
                }
              },
            })
          );
        } else {
          const result = await dispatch(
            checkTresoHasNonPointedStatus({
              projectId,
              month: convertToYYYYMM(monthForApi),
              mode,
            })
          ).unwrap();

          if (result.data.data.hasStatusNotPointed) {
            dispatch(openModal("tresorerie-lock-error-modal"));
          } else {
            dispatch(
              openModal("tresorerie-lock-success-modal", {
                onConfirm: async () => {
                  try {
                    await dispatch(
                      lockTresoPeriod({
                        projectId,
                        body: {
                          lockMonth: convertToYYYYMM(monthForApi),
                          mode,
                        },
                      })
                    ).unwrap();

                    message.success(
                      `Période ${monthForApi} verrouillée avec succès`
                    );

                    dispatch(closeModal("tresorerie-lock-success-modal"));
                  } catch (error) {
                    console.error("Error locking treso period:", error);
                    message.error("Erreur lors du verrouillage de la période");
                  }
                },
              })
            );
          }
        }
      } else if (lockStatus === "locked") {
        dispatch(
          openModal("resultat-unlock-confirm-modal", {
            onConfirm: async () => {
              try {
                await dispatch(
                  unlockComptaPeriod({
                    projectId,
                    body: {
                      unlockMonth: convertToYYYYMM(monthForApi),
                      mode,
                    },
                  })
                ).unwrap();

                message.success(`Période ${monthForApi} déverrouillée avec succès`);

                dispatch(closeModal("resultat-unlock-confirm-modal"));
              } catch (error) {
                console.error("Error unlocking compta period:", error);
                message.error("Erreur lors du déverrouillage de la période");
              }
            },
          })
        );
      } else {
        // Not locked - validate first, then show lock confirmation
        const result = await dispatch(
          checkComptaHasNonReelTypes({
            projectId,
            month: convertToYYYYMM(monthForApi),
            mode,
          })
        ).unwrap();

        if (result.data.data.hasTypeDifferentFromReel) {
          dispatch(openModal("resultat-lock-error-modal"));
        } else {
          dispatch(
            openModal("resultat-lock-success-modal", {
              onConfirm: async () => {
                try {
                  await dispatch(
                    lockComptaPeriod({
                      projectId,
                      body: {
                        lockMonth: convertToYYYYMM(monthForApi),
                        mode,
                      },
                    })
                  ).unwrap();

                  message.success(`Période ${monthForApi} verrouillée avec succès`);

                  dispatch(closeModal("resultat-lock-success-modal"));
                } catch (error) {
                  console.error("Error locking compta period:", error);
                  message.error("Erreur lors du verrouillage de la période");
                }
              },
            })
          );
        }
      }
    } catch (error) {
      console.error("Error checking lock status:", error);
    }
  };

  const iconSrc = lockStatus === "locked" ? redToggle : greenToggle;
  const altText = lockStatus === "locked" ? "Locked" : "Unlocked";
  const isDisabledLook = isDisplayOnly || RegroupmentValue !== 1;
  const cursorStyle = isDisabledLook
    ? isDisplayOnly
      ? "default"
      : "not-allowed"
    : "pointer";

  return (
    <Tooltip title={tooltipMessage} mouseEnterDelay={0.5}>
      <img
        src={iconSrc}
        alt={altText}
        onClick={isDisplayOnly ? undefined : handleLockClick}
        style={{
          width: "12px",
          height: "12px",
          cursor: cursorStyle,
          opacity: isDisabledLook ? 0.7 : 1,
          filter: "none",
          transform:
            lockStatus === "locked" ? "rotate(-90deg)" : "rotate(90deg)",
        }}
      />
    </Tooltip>
  );
}

export default LockIcon;
